<template>
  <section
    class="relative w-full md:hidden page-hero-mobile-container mt-2"
    :section="section"
    :id="String(id)"
    :__typename="__typename"
  >
    <div v-if="!isMounted" class="relative banner-placeholder w-full">
      <div class="text-center py-4 text-gray-400">Loading banner...</div>
    </div>

    <div v-if="hasSwiperData && simpleMode" class="simple-banner">
      <template v-for="(slide, index) in swiperData" :key="index">
        <img
          v-if="slide"
          :src="getMediaUrl(slide)"
          loading="lazy"
          :alt="slide.caption || content?.heading || 'Banner image'"
          class="w-full banner-image"
          :class="{
            'blur-sm':
              !imageStatus[slide.url] || imageStatus[slide.url] === 'loading',
          }"
          @error="(e) => handleImageError(e, slide)"
          @load="(e) => handleImageLoad(e, slide)"
        />
      </template>

      <template v-for="(slide, index) in swiperData" :key="'fallback-' + index">
        <div
          v-if="
            !slide || !getMediaUrl(slide) || imageStatus[slide.url] === 'error'
          "
          class="banner-fallback"
        >
          <div
            class="flex flex-col items-center justify-center p-8 bg-gray-100 h-full w-full"
          >
            <p class="text-lg font-bold">
              {{ slide?.caption || content?.heading || "Banner" }}
            </p>
            <p class="text-sm text-gray-500">Image could not be loaded</p>
          </div>
        </div>
      </template>
    </div>

    <ClientOnly v-if="!simpleMode">
      <div v-if="hasSwiperData" class="slider-wrapper">
        <!-- Navigation buttons positioned outside the slider -->
        <div class="navigation-container">
          <!-- <div class="swiper-button-prev custom-nav-button"></div> -->
          <div class="swiper-container">
            <Swiper
              ref="swiper"
              :modules="[Autoplay, EffectFade, Navigation, Pagination]"
              :slides-per-view="1"
              :loop="true"
              :effect="'fade'"
              :autoplay="{ delay: 3000, disableOnInteraction: false }"
              :navigation="{
                prevEl: '.swiper-button-prev',
                nextEl: '.swiper-button-next',
              }"
              :pagination="{
                clickable: true,
                dynamicBullets: false,
              }"
              @swiper="onSwiperInit"
              @slideChange="onSlideChange"
            >
              <SwiperSlide v-for="(slide, index) in swiperData" :key="index">
                <div class="relative">
                  <!-- Clickable image -->
                  <div
                    v-if="slide"
                    class="image-wrapper cursor-pointer"
                    @click="handleImageClick(index)"
                  >
                    <img
                      :src="getMediaUrl(slide)"
                      loading="lazy"
                      :alt="slide.caption || content?.heading || 'Banner image'"
                      class="slide-image"
                      :class="{
                        'blur-sm':
                          !imageStatus[slide.url] ||
                          imageStatus[slide.url] === 'loading',
                      }"
                      @error="(e) => handleImageError(e, slide)"
                      @load="(e) => handleImageLoad(e, slide)"
                    />
                  </div>

                  <!-- Error state -->
                  <div
                    v-if="
                      !slide ||
                      !getMediaUrl(slide) ||
                      imageStatus[slide.url] === 'error'
                    "
                    class="absolute inset-0 flex items-center justify-center bg-gray-100"
                  >
                    <div class="text-center">
                      <div class="text-gray-400">
                        {{
                          slide?.caption || content?.heading || "Banner image"
                        }}
                      </div>
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            </Swiper>
          </div>
          <!-- <div class="swiper-button-next custom-nav-button"></div> -->
        </div>
      </div>
      <div
        v-else-if="isMounted && !hasSwiperData"
        class="bg-gray-100 p-4 text-center"
      >
        <p>No banner images available</p>
      </div>
    </ClientOnly>
  </section>
</template>

<script setup lang="ts">
import { Swiper, SwiperSlide } from "swiper/vue";
import { Autoplay, EffectFade, Navigation, Pagination } from "swiper/modules";
import { useCustomStrapiMedia } from "~/composables/useStrapiMedia";
import { ref, computed, onMounted, nextTick } from "vue";
import { useRouter } from "vue-router";
import "swiper/css";
import "swiper/css/effect-fade";
import "swiper/css/navigation";
import "swiper/css/pagination";

interface Slide {
  caption?: string;
  url: string;
  targetUrl?: string;
  formats?: {
    medium?: { url: string };
    small?: { url: string };
  };
}

const props = defineProps({
  content: {
    type: Object,
    required: true,
    default: () => ({ heading: "", swiper: [] }),
  },
  section: { type: Object, default: () => ({}) },
  id: { type: [String, Number], default: null },
  __typename: { type: String, default: null },
});

const isMounted = ref(false);
const swiperData = ref<Slide[]>([]);
const simpleMode = ref(false);
const debugMode = ref(false);
const imageStatus = ref<Record<string, "loading" | "loaded" | "error">>({});
const swiperImageTargetURLs = ref<Array<Record<string, string>>>([]);
const activeSlide = ref(0);
const swiper = ref(null);

const hasSwiperData = computed(
  () =>
    isMounted.value &&
    Array.isArray(swiperData.value) &&
    swiperData.value.length > 0
);

const getMediaUrl = (slide: Slide): string => {
  try {
    if (slide.formats?.medium?.url)
      return useCustomStrapiMedia(slide.formats.medium.url);
    return useCustomStrapiMedia(slide.url);
  } catch (err) {
    console.error("Error resolving media URL:", err);
    return "";
  }
};

const handleImageError = (event: Event, slide: Slide) => {
  if (slide?.url) imageStatus.value[slide.url] = "error";
};

const handleImageLoad = (event: Event, slide: Slide) => {
  if (slide?.url) imageStatus.value[slide.url] = "loaded";
};

const router = useRouter();

const onSwiperInit = (swiperInstance) => {
  console.log("Swiper initialized");
  activeSlide.value = 0; // Initialize to first slide
};

const onSlideChange = (swiperInstance) => {
  if (swiper.value && swiper.value.swiper) {
    const index = swiper.value.swiper.activeIndex;
    console.log("Slide changed to:", index);
    activeSlide.value = index;
  }
};

const getUrlForIndex = (index: number): string | null => {
  // Get the object at the same index
  const urlObj = swiperImageTargetURLs.value[index];
  // Get the URL using the index as key
  const url = urlObj ? urlObj[index.toString()] : null;
  console.log(`URL lookup - Index: ${index}, Object:`, urlObj, "URL:", url);
  return url;
};

const handleImageClick = (index: number) => {
  const url = getUrlForIndex(index);
  console.log(`Clicked image ${index}, URL:`, url);
  if (url) {
    router.push(url);
  }
};

const navigateToSlide = (index: number) => {
  console.log("Navigating to slide:", index);
  if (swiper.value && swiper.value.swiper) {
    swiper.value.swiper.slideTo(index);
    activeSlide.value = index;
  } else {
    console.log("Swiper not available", swiper.value);
  }
};

const handleSectionClick = () => {
  const url = getUrlForIndex(activeSlide.value);
  if (url) {
    router.push(url);
  } else {
    // Fallback to FAQ page if no URL specified
    router.push("/catch-connect-rebrand-faqs");
  }
};

// In onMounted, handle the array structure
onMounted(async () => {
  await nextTick();
  console.log("Initial swiper data:", props.content.swiper);
  console.log(
    "Initial target URLs from props:",
    JSON.stringify(props.content.swiperImageTargetURLs, null, 2)
  );

  // Add an extra check to initialize swiper after mounting
  setTimeout(() => {
    console.log("Checking swiper initialization");
    if (swiper.value) {
      console.log("Swiper instance available:", swiper.value);
    } else {
      console.log("Swiper instance not available");
    }
  }, 1000);

  // Initialize the target URLs and swiper data
  if (Array.isArray(props.content?.swiperImageTargetURLs)) {
    swiperImageTargetURLs.value = props.content.swiperImageTargetURLs;
  }

  if (Array.isArray(props.content?.swiper)) {
    swiperData.value = props.content.swiper.filter(
      (slide) => slide && typeof slide === "object" && slide.url
    );

    swiperData.value.forEach((slide, index) => {
      imageStatus.value[slide.url] = "loading";
    });
  }

  // Validate URL structure
  if (props.content?.swiperImageTargetURLs) {
    props.content.swiperImageTargetURLs.forEach((urlObj, idx) => {
      console.log(`URL object ${idx}:`, urlObj);
      console.log(
        `Has property ${idx}:`,
        urlObj.hasOwnProperty(idx.toString())
      );
      console.log(`Value for ${idx}:`, urlObj[idx.toString()]);
    });
  }

  // Set up swiper events after a short delay to ensure swiper is initialized
  setTimeout(() => {
    if (swiper.value && swiper.value.swiper) {
      console.log("Setting up swiper events");
      swiper.value.swiper.on("slideChange", () => {
        console.log("Slide changed to:", swiper.value.swiper.activeIndex);
        activeSlide.value = swiper.value.swiper.activeIndex;
      });
    } else {
      console.log("Swiper not initialized yet");
    }
  }, 500);

  isMounted.value = true;
});
</script>

<style>
/* Slider layout */
.slider-wrapper {
  width: 100%;
  position: relative;
}

.navigation-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.swiper-container {
  width: calc(100% - 100px);
  position: relative;
  overflow: hidden;
}

/* Image styles */
.image-wrapper {
  width: 100%;
  position: relative;
}

.slide-image {
  width: 100%;
  height: auto;
  display: block;
}

.cursor-pointer {
  cursor: pointer;
}

/* Add some space below the slider for pagination */
.swiper {
  margin-bottom: 10px;
}

/* Custom navigation styling */
.custom-nav-button {
  position: relative !important;
  top: auto !important;
  left: auto !important;
  right: auto !important;
  margin: 0 !important;
  background-color: #8800d4 !important; /* Purple to match pagination */
  color: white !important;
  border-radius: 50% !important;
  width: 40px !important;
  height: 40px !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2) !important;
  z-index: 30 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  cursor: pointer !important;
}

.custom-nav-button:hover {
  background-color: #8800d4 !important; /* Darker purple on hover */
  transform: scale(1.1) !important;
}

.custom-nav-button::after {
  font-size: 18px !important;
  font-weight: bold !important;
  color: white !important;
}

/* Custom pagination styles with purple active state */
:root {
  --swiper-theme-color: #6b46c1;
  --swiper-pagination-color: #6b46c1;
  --swiper-pagination-bullet-inactive-color: #ccc;
  --swiper-pagination-bullet-inactive-opacity: 1;
}

.swiper-pagination {
  position: relative !important;
  margin-top: 20px !important;
  bottom: 0 !important;
  display: flex !important;
  justify-content: center !important;
  width: 100% !important;
}

.swiper-pagination-bullet {
  width: 12px !important;
  height: 12px !important;
  margin: 0 5px !important;
  background-color: #ccc !important;
  opacity: 1 !important;
  cursor: pointer !important;
  transition: all 0.2s ease !important;
  border-radius: 50% !important; /* Circular shape */
}

.swiper-pagination-bullet-active {
  background-color: #6b46c1 !important; /* Purple color for active state */
  opacity: 1 !important;

  border: none !important;
}
</style>

<style scoped lang="scss">
.page-hero-mobile-container {
  width: 100%;
  margin-bottom: 0;
  min-height: 100px;
  position: relative;
  z-index: 1;
}
.simple-banner {
  width: 100%;
  position: relative;
  overflow: hidden;
  .banner-image {
    width: 100%;
    height: auto;
    display: block;
    max-height: 450px;
    object-fit: contain;
    transition: filter 0.3s ease;
  }
}
.banner-fallback {
  width: 100%;
  height: 200px;
  background-color: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  border: 1px dashed #ccc;
}
.swiper-container {
  width: 100%;
}
.swiper {
  width: 100%;
  --swiper-navigation-color: #000;
  --swiper-pagination-color: #000;
  --swiper-navigation-size: 30px;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.banner-placeholder {
  height: 200px;
  background-color: #f9f9f9;
  display: flex;
  align-items: center;
  justify-content: center;
}
@media (max-width: 768px) {
  .page-hero-container {
    margin-bottom: 10px;
  }
  .simple-banner .banner-image {
    max-height: 200px;
  }
}
</style>
